/* eslint-disable no-param-reassign */
import { createSlice, PayloadAction } from '@reduxjs/toolkit'

import { get } from 'utils/lodash'

import { readPreferences } from './cardVisibilityStorage'
import { CardVisibilityState, createCardVisibilityReducers, createCardVisibilitySelectors } from './createCardVisibilitySlice'

// Keys CardFilterMenu can toggle. Matches the design's supported card
// attributes (cardAttributes.js); other entries in the registry render
// as disabled rows since they're not yet wired through the FE.
export const CARD_TOGGLE_KEYS = [
  'serviceAddress',
  'serviceName',
  'recurrence',
  'measure',
  'action',
  'tags',
  'overflowMenu',
  'status',
  'materials',
  'locationName',
  'workOrder',
  'purchaseOrder',
  'notes',
  'exceptions',
  'images',
  'video',
  'containers',
  'billingAddress',
  'businessLine',
] as const

export type CardToggleKey = typeof CARD_TOGGLE_KEYS[number]

type LayoutSliceKey = 'withUnassigned' | 'withEnded' | 'withDeactivated' | 'withMaster'
type MapLayerSliceKey = 'withSequenceLine' | 'withDriverPath' | 'withContainerLocation' | 'withStops' | 'withAssignableStops'

export type PersistentMasterRouteState = CardVisibilityState<CardToggleKey> & Record<LayoutSliceKey | MapLayerSliceKey, boolean>

const initialState: PersistentMasterRouteState = {
  // ── Card-content toggles ────────────────────────────────────────────────
  // Match the hh-master-sequence CardFilterMenu surface. Defaults follow the
  // designs' cardAttributes registry — visible-by-default attrs ride here at
  // `true`; action + tags stay at `true` so existing aegis users don't lose
  // chrome they're already accustomed to (the design's defaults of `false`
  // for those two are a fresh-slate choice; we honour the dispatcher's
  // muscle memory until they opt out).
  withServiceAddress: true,
  withServiceName: true,
  withRecurrence: true,
  withMeasure: true,
  withAction: true,
  withTags: true,
  withOverflowMenu: true,
  // Newly-wired attributes — the daily payload already carries their data;
  // master rows skip the work-order-level badges via the workOrderId gate
  // in StopCard. Defaults match the cardAttributes registry.
  withStatus: false,
  withMaterials: false,
  withLocationName: true,
  withWorkOrder: false,
  withPurchaseOrder: false,
  withNotes: false,
  withExceptions: false,
  withImages: false,
  withVideo: false,
  // Flipping containers on refetches stops with includeContainers=true.
  withContainers: false,
  withBillingAddress: false,
  withBusinessLine: false,
  // ── Layout / column toggles ─────────────────────────────────────────────
  withUnassigned: false,
  withEnded: false,
  withDeactivated: false,
  // Master column visibility — true by default since the master sequence is
  // the editor's primary surface. Dispatchers can collapse it when they only
  // need to fan out across the daily lanes (matches the prototype's
  // SubToolbar Daily/Master toggle).
  withMaster: true,
  // ── Map layer toggles ───────────────────────────────────────────────────
  // Visibility of overlay layers on the master-sequence Map tab. Defaults
  // mirror the prior in-memory defaults from masterRouteSlice so muscle
  // memory survives the move into persisted storage.
  withSequenceLine: false,
  withDriverPath: true,
  withContainerLocation: true,
  // Stop pill markers — the primary surface on the map, so true by default.
  withStops: true,
  // Assignable (unassigned pool / in-focus source route) markers on the map.
  // Lets the dispatcher hide the assignable overlay to focus on the current
  // route, or show it to compare before pulling stops in (WOLV-4642).
  withAssignableStops: true,
}

export const MASTER_ROUTE_STORAGE_KEY = 'hh.masterSequence.boardPreferences'

export const readStoredMasterRoutePreferences = (): PersistentMasterRouteState =>
  readPreferences(MASTER_ROUTE_STORAGE_KEY, 'persistentMasterRoute', initialState)

const masterRouteSlice = createSlice({
  name: 'persistentMasterRoute',
  initialState: { ...initialState },
  reducers: {
    setWithTags: (state, action: PayloadAction<boolean>) => {
      state.withTags = action.payload
    },
    setWithAction: (state, action: PayloadAction<boolean>) => {
      state.withAction = action.payload
    },
    setWithServiceAddress: (state, action: PayloadAction<boolean>) => {
      state.withServiceAddress = action.payload
    },
    setWithServiceName: (state, action: PayloadAction<boolean>) => {
      state.withServiceName = action.payload
    },
    setWithRecurrence: (state, action: PayloadAction<boolean>) => {
      state.withRecurrence = action.payload
    },
    setWithMeasure: (state, action: PayloadAction<boolean>) => {
      state.withMeasure = action.payload
    },
    setWithOverflowMenu: (state, action: PayloadAction<boolean>) => {
      state.withOverflowMenu = action.payload
    },
    // Generic card-attribute toggles, keyed off attribute name to match the
    // design's `?hide=` URL contract. Shared with the board-v2 slice.
    ...createCardVisibilityReducers<PersistentMasterRouteState, CardToggleKey>(CARD_TOGGLE_KEYS, ['containers']),
    setWithUnassigned: (state, action: PayloadAction<boolean>) => {
      state.withUnassigned = action.payload
    },
    setWithEnded: (state, action: PayloadAction<boolean>) => {
      state.withEnded = action.payload
    },
    setWithDeactivated: (state, action: PayloadAction<boolean>) => {
      state.withDeactivated = action.payload
    },
    setWithMaster: (state, action: PayloadAction<boolean>) => {
      state.withMaster = action.payload
    },
    setWithSequenceLine: (state, action: PayloadAction<boolean>) => {
      state.withSequenceLine = action.payload
    },
    setWithDriverPath: (state, action: PayloadAction<boolean>) => {
      state.withDriverPath = action.payload
    },
    setWithContainerLocation: (state, action: PayloadAction<boolean>) => {
      state.withContainerLocation = action.payload
    },
    setWithStops: (state, action: PayloadAction<boolean>) => {
      state.withStops = action.payload
    },
    setWithAssignableStops: (state, action: PayloadAction<boolean>) => {
      state.withAssignableStops = action.payload
    },
  },
})

export const {
  setWithTags,
  setWithAction,
  setWithServiceAddress,
  setWithServiceName,
  setWithRecurrence,
  setWithMeasure,
  setWithOverflowMenu,
  setCardAttributeVisible,
  setAllCardAttributesVisible,
  hydrateFromStorage,
  restoreFromStorage,
  setWithUnassigned,
  setWithEnded,
  setWithDeactivated,
  setWithMaster,
  setWithSequenceLine,
  setWithDriverPath,
  setWithContainerLocation,
  setWithStops,
  setWithAssignableStops,
} = masterRouteSlice.actions

// Narrow root-state shape so selectors and tests don't need the full RootState.
export interface PersistentMasterRouteRootStateSlice {
  persistentMasterRoute?: PersistentMasterRouteState
}

type Selector<T> = (state: PersistentMasterRouteRootStateSlice) => T

const { selectWith, selectCardAttributeVisibility: selectVisibility } = createCardVisibilitySelectors<CardToggleKey>({
  statePath: 'persistentMasterRoute',
  toggleKeys: CARD_TOGGLE_KEYS,
  defaults: initialState,
})

// Card-attribute selectors — derived from the shared factory.
export const selectWithTags: Selector<boolean> = selectWith('tags')
export const selectWithAction: Selector<boolean> = selectWith('action')
export const selectWithServiceAddress: Selector<boolean> = selectWith('serviceAddress')
export const selectWithServiceName: Selector<boolean> = selectWith('serviceName')
export const selectWithRecurrence: Selector<boolean> = selectWith('recurrence')
export const selectWithMeasure: Selector<boolean> = selectWith('measure')
export const selectWithOverflowMenu: Selector<boolean> = selectWith('overflowMenu')
export const selectWithStatus: Selector<boolean> = selectWith('status')
export const selectWithMaterials: Selector<boolean> = selectWith('materials')
export const selectWithLocationName: Selector<boolean> = selectWith('locationName')
export const selectWithWorkOrder: Selector<boolean> = selectWith('workOrder')
export const selectWithPurchaseOrder: Selector<boolean> = selectWith('purchaseOrder')
export const selectWithNotes: Selector<boolean> = selectWith('notes')
export const selectWithExceptions: Selector<boolean> = selectWith('exceptions')
export const selectWithImages: Selector<boolean> = selectWith('images')
export const selectWithVideo: Selector<boolean> = selectWith('video')
export const selectWithContainers: Selector<boolean> = selectWith('containers')
export const selectWithBillingAddress: Selector<boolean> = selectWith('billingAddress')
export const selectWithBusinessLine: Selector<boolean> = selectWith('businessLine')

// Layout / map-layer selectors — master-route specific, not card toggles.
export const selectWithUnassigned: Selector<boolean> = state => get(state, 'persistentMasterRoute.withUnassigned', false)
export const selectWithEnded: Selector<boolean> = state => get(state, 'persistentMasterRoute.withEnded', false)
export const selectWithDeactivated: Selector<boolean> = state => get(state, 'persistentMasterRoute.withDeactivated', false)
export const selectWithMaster: Selector<boolean> = state => get(state, 'persistentMasterRoute.withMaster', true)
export const selectWithSequenceLine: Selector<boolean> = state => get(state, 'persistentMasterRoute.withSequenceLine', false)
export const selectWithDriverPath: Selector<boolean> = state => get(state, 'persistentMasterRoute.withDriverPath', true)
export const selectWithContainerLocation: Selector<boolean> = state => get(state, 'persistentMasterRoute.withContainerLocation', true)
export const selectWithStops: Selector<boolean> = state => get(state, 'persistentMasterRoute.withStops', true)
export const selectWithAssignableStops: Selector<boolean> = state => get(state, 'persistentMasterRoute.withAssignableStops', true)

// Memoized {attributeKey: visible} map for CardFilterMenu + StopCard, derived
// from the shared factory (createSelector inside createCardVisibilitySlice) — it
// only recomputes when a toggle changes, so cards share one reference per dispatch.
export const selectCardAttributeVisibility: Selector<Record<CardToggleKey, boolean>> = selectVisibility

export default masterRouteSlice.reducer
